import type { ChangePasswordRequest, Device, LoginResponse, MeResponse } from '@bssb/contracts';
import { apiRequest } from '@/offline/api-client';

/**
 * Panggilan autentikasi (T-P7-04/05/28).
 *
 * Ini satu-satunya modul selain `syncEngine` dan `mediaQueue` yang menyentuh
 * jaringan, dan alasannya jelas: login memang tidak bisa dikerjakan offline.
 * Layar tetap tidak boleh memanggil apa pun di sini untuk membaca data nasabah.
 */

export interface LoginInput {
  username: string;
  password: string;
  deviceFingerprint: string;
  deviceLabel: string;
}

export const authApi = {
  login: (input: LoginInput) =>
    apiRequest<LoginResponse>('/auth/login', { method: 'POST', body: input }),

  refresh: () =>
    apiRequest<Omit<LoginResponse, 'passwordChangeRequired' | 'deviceId'>>('/auth/refresh', {
      method: 'POST',
      body: {},
    }),

  logout: () =>
    apiRequest<{ wipeLocalCache: boolean }>('/auth/logout', { method: 'POST', body: {} }),

  me: () => apiRequest<MeResponse>('/auth/me'),

  changePassword: (input: ChangePasswordRequest) =>
    apiRequest<{ changed: boolean; reauthenticationRequired: boolean }>('/auth/change-password', {
      method: 'POST',
      body: input,
    }),

  registerDevice: (input: { deviceFingerprint: string; deviceLabel: string }) =>
    apiRequest<{ id: string; deviceLabel: string | null }>('/auth/devices', {
      method: 'POST',
      body: input,
    }),

  listDevices: () => apiRequest<Device[]>('/auth/devices'),

  revokeDevice: (id: string) =>
    apiRequest<void>(`/auth/devices/${id}`, { method: 'DELETE', body: { requestWipe: true } }),

  webauthn: {
    registerOptions: (deviceId: string) =>
      apiRequest<Record<string, unknown>>('/auth/webauthn/register/options', {
        method: 'POST',
        body: { deviceId },
      }),

    registerVerify: (deviceId: string, response: unknown) =>
      apiRequest<{ verified: boolean }>('/auth/webauthn/register/verify', {
        method: 'POST',
        body: { deviceId, response },
      }),

    loginOptions: (username: string) =>
      apiRequest<Record<string, unknown>>('/auth/webauthn/login/options', {
        method: 'POST',
        body: { username },
      }),

    loginVerify: (username: string, response: unknown) =>
      apiRequest<LoginResponse>('/auth/webauthn/login/verify', {
        method: 'POST',
        body: { username, response },
      }),
  },
};
